/**
 * 個人ホームダッシュボード「自分の今日のタスク」を導出する hook (P-G)。
 *
 * - 個別説明予定 (本日 + 7日以内、briefingAssigneeUid === 自分)
 * - 入金待ち (assigneeUid === 自分の Deal の invoices で status='unpaid')
 * - シリアル未入力 (assigneeUid === 自分の Deal の deliveries で serialNumbers 欠損)
 * - 入荷・検品・発送 (2026-10-30〜、自分の Deal の発注で 入荷待ち / 検品待ち / 発送待ち)
 * - 停滞案件 (assigneeUid === 自分の Deal で lastTouchedAt が 7日以上前 + 未クローズ)
 *
 * 各 invoice/delivery を Deal 横断で取得するため、collectionGroup を直接叩くシンプル実装にする。
 */
import type {
  Deal,
  Delivery,
  IsoOrTimestamp,
  Invoice,
  ProductCategory,
  PurchaseOrder,
} from '@goenhub/shared';
import { countDeliverySerials, inspectionProgress } from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import { collection, collectionGroup, getDocs, orderBy, query, where } from 'firebase/firestore';

import { db } from '@/lib/firebase';

export interface BriefingTask {
  dealId: string;
  dealTitle: string;
  contactId: string;
  scheduledAt: Date;
  isToday: boolean;
}

export interface InvoiceTask {
  dealId: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  dueDate: Date | null;
  isOverdue: boolean;
}

export interface SerialTask {
  dealId: string;
  deliveryId: string;
  deliveryNumber: string;
  totalSerials: number;
  filledSerials: number;
}

/** 入荷・検品・発送の待ち (発注 1 件ごと)。 */
export interface FulfillmentTask {
  dealId: string;
  dealTitle: string;
  poId: string;
  poNumber: string;
  /** ordered = 入荷待ち / received = 検品待ち / inspected = 発送待ち */
  status: 'ordered' | 'received' | 'inspected';
  /** 補足 (発注からの日数・未入力の台数など) */
  detail: string;
}

export interface StaleDealTask {
  dealId: string;
  title: string;
  stage: Deal['stage'];
  daysSinceTouch: number;
}

export interface DashboardTasks {
  briefings: BriefingTask[];
  invoices: InvoiceTask[];
  serials: SerialTask[];
  fulfillment: FulfillmentTask[];
  staleDeals: StaleDealTask[];
  /** 全タスクの総数 */
  total: number;
}

const STALE_THRESHOLD_DAYS = 7;
const BRIEFING_LOOKAHEAD_DAYS = 7;

function tsToDate(v: IsoOrTimestamp | null | undefined): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function daysBetween(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / (24 * 60 * 60 * 1000));
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function useDashboardTasks(uid: string | null | undefined) {
  return useQuery<DashboardTasks>({
    queryKey: ['dashboard_tasks', uid],
    enabled: !!uid,
    queryFn: async () => {
      if (!uid) {
        return {
          briefings: [],
          invoices: [],
          serials: [],
          fulfillment: [],
          staleDeals: [],
          total: 0,
        };
      }

      // 自分が assignee or briefing assignee の Deal を取得
      // (Firestore は OR を直接サポートしないので 2 クエリ + マージ)
      const [assigneeSnap, briefingSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, 'deals'),
            where('assigneeUid', '==', uid),
            where('_deleted', '==', false),
          ),
        ),
        getDocs(
          query(
            collection(db, 'deals'),
            where('briefingAssigneeUid', '==', uid),
            where('_deleted', '==', false),
          ),
        ),
      ]);

      const dealMap = new Map<string, Deal & { id: string }>();
      for (const d of assigneeSnap.docs) dealMap.set(d.id, { id: d.id, ...(d.data() as Deal) });
      for (const d of briefingSnap.docs) dealMap.set(d.id, { id: d.id, ...(d.data() as Deal) });
      const myDeals = Array.from(dealMap.values());
      const myDealIds = new Set(myDeals.map((d) => d.id));

      const now = new Date();

      // 1. 個別説明予定 (本日 + 7日以内)
      const briefings: BriefingTask[] = [];
      for (const d of myDeals) {
        if (d.briefingAssigneeUid !== uid) continue;
        const at = tsToDate(d.scheduledBriefingAt);
        if (!at) continue;
        const days = daysBetween(at, now);
        if (days < -1 || days > BRIEFING_LOOKAHEAD_DAYS) continue;
        briefings.push({
          dealId: d.id,
          dealTitle: d.title || '(タイトル未設定)',
          contactId: d.contactId,
          scheduledAt: at,
          isToday: isSameDay(at, now),
        });
      }
      briefings.sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime());

      // 2. 入金待ち (collectionGroup で全 unpaid invoices を取得 → my deals でフィルタ)
      const invoiceTasks: InvoiceTask[] = [];
      try {
        const unpaidSnap = await getDocs(
          query(
            collectionGroup(db, 'invoices'),
            where('status', '==', 'unpaid'),
            orderBy('dueDate', 'asc'),
          ),
        );
        for (const docSnap of unpaidSnap.docs) {
          const inv = docSnap.data() as Invoice;
          if (!myDealIds.has(inv.dealId)) continue;
          const due = tsToDate(inv.dueDate);
          invoiceTasks.push({
            dealId: inv.dealId,
            invoiceId: docSnap.id,
            invoiceNumber: inv.invoiceNumber,
            amount: inv.amount,
            dueDate: due,
            isOverdue: due ? due.getTime() < now.getTime() : false,
          });
        }
      } catch {
        // collectionGroup インデックス未作成の場合はスキップ
      }

      // 3. シリアル未入力 (in_transit/pending な delivery で my deals に含まれるもの)
      const serialTasks: SerialTask[] = [];
      try {
        const deliveryStatuses = ['pending', 'in_transit'] as const;
        // status in [pending, in_transit] は collectionGroup + 'in' で1クエリにまとめる
        const dlSnap = await getDocs(
          query(
            collectionGroup(db, 'deliveries'),
            where('status', 'in', deliveryStatuses as unknown as string[]),
          ),
        );
        for (const docSnap of dlSnap.docs) {
          const dl = docSnap.data() as Delivery;
          if (!myDealIds.has(dl.dealId)) continue;
          // 台数基準で数える (シリアル欄が空配列の発送も未入力として拾う)
          const { total, filled } = countDeliverySerials(dl.lineItems);
          if (filled < total) {
            serialTasks.push({
              dealId: dl.dealId,
              deliveryId: docSnap.id,
              deliveryNumber: dl.deliveryNumber,
              totalSerials: total,
              filledSerials: filled,
            });
          }
        }
      } catch {
        // ignore
      }

      // 4. 入荷・検品・発送の待ち (発注を createdAt 順で全件読み、自分の Deal の分だけ拾う)
      const fulfillmentTasks: FulfillmentTask[] = [];
      try {
        const [poSnap, catSnap] = await Promise.all([
          getDocs(query(collectionGroup(db, 'purchase_orders'), orderBy('createdAt', 'desc'))),
          getDocs(collection(db, 'product_categories')),
        ]);
        const twCategoryIds = new Set(
          catSnap.docs
            .filter((c) => (c.data() as ProductCategory).name === 'TimeWaver')
            .map((c) => c.id),
        );
        const order = { inspected: 0, received: 1, ordered: 2 } as const;
        for (const docSnap of poSnap.docs) {
          const po = docSnap.data() as PurchaseOrder;
          if (!myDealIds.has(po.dealId)) continue;
          if (po.status !== 'ordered' && po.status !== 'received' && po.status !== 'inspected') {
            continue;
          }
          const deal = dealMap.get(po.dealId);
          const isTimeWaver =
            !!deal?.primaryCategoryId && twCategoryIds.has(deal.primaryCategoryId);
          let detail = '';
          if (po.status === 'ordered') {
            const at = tsToDate(po.orderedAt);
            detail = at ? `発注から ${Math.max(0, daysBetween(now, at))} 日` : '入荷待ち';
          } else if (po.status === 'received') {
            const pr = inspectionProgress(po, isTimeWaver);
            const left = pr.totalUnits - pr.completeUnits;
            detail = `入荷 ${(po.inspection?.arrivedAt ?? '').replace(/-/g, '/')} · 未入力 ${left} 台`;
          } else {
            detail = '検品済 · 発送待ち';
          }
          fulfillmentTasks.push({
            dealId: po.dealId,
            dealTitle: deal?.title || '(タイトル未設定)',
            poId: docSnap.id,
            poNumber: po.poNumber,
            status: po.status,
            detail,
          });
        }
        fulfillmentTasks.sort((a, b) => order[a.status] - order[b.status]);
      } catch {
        // ignore
      }

      // 5. 停滞 (7日超 + open + closed じゃない)
      const staleDeals: StaleDealTask[] = [];
      for (const d of myDeals) {
        if (d.status === 'closed') continue;
        const lt = tsToDate(d.lastTouchedAt);
        if (!lt) continue;
        const days = daysBetween(now, lt);
        if (days >= STALE_THRESHOLD_DAYS) {
          staleDeals.push({
            dealId: d.id,
            title: d.title || '(タイトル未設定)',
            stage: d.stage,
            daysSinceTouch: days,
          });
        }
      }
      staleDeals.sort((a, b) => b.daysSinceTouch - a.daysSinceTouch);

      const total =
        briefings.length +
        invoiceTasks.length +
        serialTasks.length +
        fulfillmentTasks.length +
        staleDeals.length;

      return {
        briefings,
        invoices: invoiceTasks,
        serials: serialTasks,
        fulfillment: fulfillmentTasks,
        staleDeals,
        total,
      };
    },
  });
}
